import { Badge, BlockStack, Box, Button, Collapsible, InlineStack, Link, Text, TextField, Tooltip } from '@shopify/polaris';
import { useEffect, useRef, useState } from 'react';
import { formatDate, formatToCurrency } from '../functions';
import { approvalsApi } from './approvalsApi';
import PoLinesDiff from './PoLinesDiff';
import ApprovalStatusBadge from './ApprovalStatusBadge';

/*
  Linked purchase orders of an incoming invoice (owner, status, received, project, totals, other invoices, line diff)
  plus candidates and a search box to link one by hand. Amounts are informational: matching never uses them.
*/
const STATUS_TONE = { RECEIVED: 'success', PARTIALLY_RECEIVED: 'attention', SENT: 'info', DRAFT: undefined, CANCELLED: 'critical' };

function PurchaseOrderHeader({ po, invoiceAmount, canEdit, onUnlink, busy, invoiceLines, peppolLines }) {
  const [open, setOpen] = useState(false);
  const delta = po.totalValue && invoiceAmount !== null ? Math.round((invoiceAmount - po.totalValue) * 100) / 100 : null;
  const deltaPct = delta !== null && po.totalValue ? Math.round((delta / po.totalValue) * 1000) / 10 : null;
  const lines = invoiceLines?.length
    ? invoiceLines
    : (peppolLines || []).map((line) => ({ tekstlijn: line.name || line.description, aantal: Number(line.quantity), VP: Number(line.netPriceAmount), ArtikelCode: line.sellersId || line.buyersId }));
  return (
    <Box padding="300" borderWidth="025" borderColor="border" borderRadius="200">
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center" wrap>
          <InlineStack gap="200" blockAlign="center">
            <Link url={`/purchases/${po.BestellingID}`} removeUnderline>
              <Text as="span" fontWeight="semibold">{`PO ${po.Bestellingnummer}`}</Text>
            </Link>
            <Badge tone={STATUS_TONE[po.statusCode]}>{po.statusName || po.statusCode}</Badge>
            {po.receiving?.isFullyReceived ? (
              <Badge tone="success">Received{po.receiving.lastReceivedAt ? ` ${formatDate(po.receiving.lastReceivedAt)}` : ''}</Badge>
            ) : po.receiving?.receivedQty > 0 ? (
              <Badge tone="attention">{`Received ${po.receiving.receivedQty} of ${po.receiving.orderedQty}`}</Badge>
            ) : (
              <Badge tone="warning">Not received yet</Badge>
            )}
            {po.matchSource ? (
              <Tooltip content={`Linked via ${po.matchSource}${po.linkedByAfkorting ? ` by ${po.linkedByAfkorting}` : ' automatically'}${po.linkedAt ? ` on ${formatDate(po.linkedAt)}` : ''}`}>
                <Badge>{po.matchSource === 'MANUAL' ? 'manual link' : 'auto link'}</Badge>
              </Tooltip>
            ) : null}
          </InlineStack>
          {canEdit ? (
            <Button size="slim" variant="plain" tone="critical" onClick={() => onUnlink(po.BestellingID)} disabled={busy}>
              Unlink
            </Button>
          ) : null}
        </InlineStack>
        <InlineStack gap="400" wrap>
          <Text variant="bodySm">
            Owner <strong>{po.ownerAfkorting || '-'}</strong>
          </Text>
          <Text variant="bodySm">
            Ordered {formatDate(po.Datum)} {po.RefKlant ? `· ref ${po.RefKlant}` : ''}
          </Text>
          {po.projectNumber ? (
            <Text variant="bodySm">
              Project{' '}
              <Link url={`/projects/${po.ProjectID}`} removeUnderline>
                🚧 {po.projectNumber} {po.projectDescription || ''}
              </Link>
            </Text>
          ) : (
            <Text variant="bodySm" tone="subdued">
              No project on the purchase order
            </Text>
          )}
          {po.costTypeName ? <Text variant="bodySm">Type {po.costTypeName}</Text> : null}
        </InlineStack>
        <InlineStack gap="400" wrap>
          <Text variant="bodySm">
            PO total <strong>{formatToCurrency(po.totalValue)}</strong>
          </Text>
          {invoiceAmount !== null ? (
            <Text variant="bodySm">
              Invoice excl. VAT <strong>{formatToCurrency(invoiceAmount)}</strong>{' '}
              {delta !== null ? <Badge tone={Math.abs(delta) < 0.01 ? 'success' : 'info'}>{`Δ ${formatToCurrency(delta)}${deltaPct !== null ? ` (${deltaPct}%)` : ''}`}</Badge> : null}
            </Text>
          ) : null}
          <Text variant="bodySm">
            Invoiced before <strong>{formatToCurrency(po.invoicedSoFar || 0)}</strong>
            {po.otherInvoices?.length ? ` on ${po.otherInvoices.length} other invoice${po.otherInvoices.length === 1 ? '' : 's'}` : ''}
          </Text>
        </InlineStack>
        {po.otherInvoices?.length ? (
          <InlineStack gap="200" wrap>
            {po.otherInvoices.map((inv) => (
              <InlineStack key={inv.id} gap="100" blockAlign="center">
                <Link url={`/invoices_in/${inv.id}`} removeUnderline>
                  {inv.number}
                </Link>
                <Text variant="bodySm" tone="subdued">
                  {formatToCurrency(inv.BedragExclBtw ?? inv.Bedragfaktuur)}
                </Text>
                <ApprovalStatusBadge status={inv.ApprovalStatus} size="small" />
              </InlineStack>
            ))}
          </InlineStack>
        ) : null}
        <Button size="slim" variant="plain" onClick={() => setOpen((v) => !v)} disclosure={open ? 'up' : 'down'}>
          {open ? 'Hide lines' : `Compare lines (${po.lines?.length || 0} on PO${lines.length ? `, ${lines.length} on invoice` : ''})`}
        </Button>
        <Collapsible open={open} id={`po-lines-${po.BestellingID}`}>
          <PoLinesDiff poLines={po.lines || []} invoiceLines={lines} />
          {!lines.length ? (
            <Text variant="bodySm" tone="subdued">
              No invoice lines available (not a Peppol document), only the purchase order lines are shown.
            </Text>
          ) : null}
        </Collapsible>
      </BlockStack>
    </Box>
  );
}

function CandidateRow({ po, onLink, busy }) {
  return (
    <InlineStack align="space-between" blockAlign="center" wrap={false}>
      <BlockStack gap="050">
        <InlineStack gap="200" blockAlign="center">
          <Link url={`/purchases/${po.BestellingID}`} removeUnderline>{`PO ${po.Bestellingnummer}`}</Link>
          <Text variant="bodySm" tone="subdued">
            {po.supplierName} · {po.ownerAfkorting || '-'} · {formatDate(po.Datum)} · {formatToCurrency(po.totalValue)}
          </Text>
          {po.statusName ? <Badge tone={STATUS_TONE[po.statusCode]}>{po.statusName}</Badge> : null}
          {po.supplierMatch === false ? <Badge tone="warning">Other supplier</Badge> : null}
        </InlineStack>
        {po.reason ? (
          <Text variant="bodySm" tone="subdued">
            {po.reason}
            {po.projectNumber ? ` · project ${po.projectNumber}` : ''}
          </Text>
        ) : po.projectNumber ? (
          <Text variant="bodySm" tone="subdued">
            Project {po.projectNumber} {po.projectDescription || ''}
          </Text>
        ) : null}
      </BlockStack>
      <Button size="slim" onClick={() => onLink(po.BestellingID)} disabled={busy}>
        Link
      </Button>
    </InlineStack>
  );
}

export default function PurchaseOrderPanel({ card, canEdit = false, onLink, onUnlink, busy = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  const linked = card?.purchaseOrders || [];
  const linkedIds = new Set(linked.map((po) => po.BestellingID));
  const candidates = (card?.suggestions?.purchaseOrders?.candidates || []).filter((po) => !linkedIds.has(po.BestellingID));
  const supplierOpen = (card?.suggestions?.purchaseOrders?.supplierOpen || []).filter((po) => !linkedIds.has(po.BestellingID));
  const invoiceAmount = card?.allocation?.basis ?? null;

  useEffect(() => {
    clearTimeout(timer.current);
    if (!query.trim()) {
      setResults([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      const response = await approvalsApi.searchPurchaseOrders(query.trim(), null);
      setResults(response.ok && Array.isArray(response.data) ? response.data.filter((po) => !linkedIds.has(po.BestellingID)) : []);
      setSearching(false);
    }, 350);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <BlockStack gap="300">
      {linked.map((po) => (
        <PurchaseOrderHeader
          key={po.BestellingID}
          po={po}
          invoiceAmount={invoiceAmount}
          canEdit={canEdit}
          onUnlink={onUnlink}
          busy={busy}
          invoiceLines={card.invoiceLines}
          peppolLines={card.peppolLines}
        />
      ))}
      {!linked.length ? (
        <Text tone="subdued">
          {candidates.length || supplierOpen.length ? 'No purchase order linked yet. Possible matches:' : 'No purchase order found for this document: probably a general cost.'}
        </Text>
      ) : null}
      {canEdit && candidates.length ? (
        <BlockStack gap="200">
          {candidates.map((po) => (
            <CandidateRow key={po.BestellingID} po={po} onLink={onLink} busy={busy} />
          ))}
        </BlockStack>
      ) : null}
      {canEdit && supplierOpen.length ? (
        <BlockStack gap="100">
          <Text variant="bodySm" tone="subdued">
            Open purchase orders of this supplier
          </Text>
          {supplierOpen.slice(0, 5).map((po) => (
            <CandidateRow key={po.BestellingID} po={po} onLink={onLink} busy={busy} />
          ))}
        </BlockStack>
      ) : null}
      {canEdit ? (
        <BlockStack gap="200">
          <TextField
            label="Link another purchase order"
            labelHidden
            placeholder="Search purchase order number, reference, project or supplier…"
            value={query}
            onChange={setQuery}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setQuery('')}
            loading={searching}
          />
          {results.slice(0, 8).map((po) => (
            <CandidateRow key={po.BestellingID} po={po} onLink={onLink} busy={busy} />
          ))}
        </BlockStack>
      ) : null}
    </BlockStack>
  );
}
